/**
 * Step 4 (Supporting evidence) — record upload, supplemental docs, summary,
 * and outro screens.
 *
 * Scaffolding for #155514 ([Eng] Step 4: Supp Evidence - Implementation-
 * Record Upload & Summary). All uploads use the V3 uploader; PMR document
 * types (per staging): Medical Treatment Record - Non-Government Facility,
 * Disability Benefits Questionnaire (DBQ), VA 21-4142 Authorization for
 * Release of Information, Other.
 */
import React from 'react';
import * as claimExamsInfo from '../../../shared/pages/claimExamsInfo';
import * as evidenceChoiceIntro from '../../../shared/pages/evidenceChoiceIntroPage';
import { standardTitle } from '../../../form0781/content';
import { confirmEvidenceRemovalPage } from '../../content/confirmEvidenceRemovalPage';
import { buildItemGroups } from '../../content/evidenceRemovalContent';
import SupportingEvidenceSummary from '../../content/supportingEvidenceSummary';
import { privateMedicalRecordsUploadPages } from './privateMedicalRecordsUploadPages';
import {
  additionalEvidenceUploadPages,
  additionalEvidenceBypassedIntro,
  ADDITIONAL_EVIDENCE_INTRO_PATH,
  ADDITIONAL_EVIDENCE_LOOP,
} from './additionalEvidenceUploadPages';
import { hasAdditionalEvidence, placeholderSchema } from './utils';

const ADDITIONAL_EVIDENCE_ARRAY_PATH = ADDITIONAL_EVIDENCE_LOOP.arrayPath;

/**
 * Destructive modal for /supporting-evidence/additional-evidence-intro
 * Displays when user changes their answer from Yes to No and clicks Continue,
 * and they have already uploaded additional evidence
 */
const AdditionalEvidenceRemovalPage = confirmEvidenceRemovalPage({
  shouldConfirm: formData =>
    formData?.['view:selectableEvidenceTypes']?.['view:hasOtherEvidence'] ===
      false && (formData?.[ADDITIONAL_EVIDENCE_ARRAY_PATH] || []).length > 0,
  getItemGroups: formData =>
    buildItemGroups([
      {
        items: formData?.[ADDITIONAL_EVIDENCE_ARRAY_PATH] || [],
        kind: 'document',
        phrase: 'delete these files you uploaded',
      },
    ]),
  removeItems: formData => {
    const updated = { ...formData };
    delete updated[ADDITIONAL_EVIDENCE_ARRAY_PATH];
    return updated;
  },
  revertSelection: formData => ({
    ...formData,
    'view:selectableEvidenceTypes': {
      ...(formData?.['view:selectableEvidenceTypes'] || {}),
      'view:hasOtherEvidence': true,
    },
  }),
  modalTitle: 'Cancel uploading files?',
  modalIntro: (
    <p>
      You can choose not to include supporting documents and additional forms.
    </p>
  ),
  primaryButtonText: 'Cancel and delete',
  secondaryButtonText: 'Keep files',
  alertText: 'We’ve deleted the documents you uploaded supporting your claim.',
});

export const uploadAndSummaryPages = {
  // Only displayed if "I'll upload my records myself" was selected on
  // /other-providers-sharing-options. File upload in the List & Loop
  // (Multiple Responses) arrayBuilder pattern, modeled on v1's
  // additionalDocumentsListLoopPages (summary page + per-item upload page
  // with a decoupled document-type select).
  ...privateMedicalRecordsUploadPages,
  // Same as current flow. Decision point: gates the additional evidence
  // upload/summary pages. Matches the v1 evidenceChoiceIntro page (shared
  // module), including the Yes/No radio.
  additionalEvidenceIntro: {
    path: ADDITIONAL_EVIDENCE_INTRO_PATH,
    title:
      'Supporting documents and additional forms for your disability claim',
    uiSchema: evidenceChoiceIntro.uiSchema,
    schema: evidenceChoiceIntro.schema,
    // Intercepts Continue with a destructive confirmation modal when "No" is
    // selected and documents were already uploaded.
    CustomPage: AdditionalEvidenceRemovalPage,
    CustomPageReview: null,
    // override: bypass the loop's intro page. "Yes" should go to the first page of the add loop, or the loop summary
    // if documents already exist).
    ...additionalEvidenceBypassedIntro.decisionPage(hasAdditionalEvidence),
  },
  // Displayed if "Yes" was selected on /additional-evidence-intro.
  ...additionalEvidenceUploadPages,
  // Summary of all supporting evidence provided in Step 4.
  supportingEvidenceSummary: {
    path: 'supporting-evidence/summary',
    title: 'Summary of supporting evidence for your disability claim',
    uiSchema: {
      'ui:title': standardTitle(
        'Summary of supporting evidence for your disability claim',
      ),
      'ui:description': SupportingEvidenceSummary,
    },
    schema: placeholderSchema,
  },
  // Next steps in your claim process. Outro of Step 4, so it lives under
  // /supporting-evidence with the rest of the chapter (IA review, #157284).
  howClaimExamsWork: {
    path: 'supporting-evidence/claim-exam',
    title: 'How claim exams work',
    uiSchema: claimExamsInfo.uiSchema,
    schema: claimExamsInfo.schema,
  },
};

export default uploadAndSummaryPages;
